import SidebarCollapse from "@builder/components/app/SidebarCollapse";
import { EditableProps } from "@builder/components/editable/editableProps";
import { getStyle, getStylePath } from "@builder/components/editable/helpers";
import { getValueUnit } from "@builder/components/general/editUtils";
import SelectInput from "@builder/components/general/SelectInput";
import UnitInput from "@builder/components/general/UnitInput";
import { getParentDirection, getSelectedNode } from "@builder/store/slices/BuilderSlice/builderSelectors";
import { updateNodeProperty } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { GlobalBlockTypes, NodeSizes } from "@common/build/types";
import { useEffect } from "react";

interface SizeProps extends EditableProps {
    forceMode?: { width?: NodeSizes; height?: NodeSizes };
}

const Size = ({ title, path, excluded, noWrapper, parent, stateMode, forceMode }: SizeProps) => {
    const dispatch = useAppDispatch();
    const node = useAppSelector(getSelectedNode);
    const cvMode = useAppSelector(state => state.builder.conditionalVariationMode);
    const cvModeIndex = cvMode?.id === node?.id ? cvMode.index : null;

    const baseLayerExclude = useAppSelector(state => state.builder.build.globalBlockType) === GlobalBlockTypes.GB_PRINT ? ["fill"] : [];

    useEffect(() => {
        if (baseLayerExclude.length > 0) {
            if (node?.nodeSize?.width && baseLayerExclude.includes(node?.nodeSize?.width)) {
                dispatch(updateNodeProperty({ propertyPath: ["nodeSize", "width"], value: NodeSizes.CUSTOM }));
            }

            if (node?.nodeSize?.height && baseLayerExclude.includes(node?.nodeSize?.height)) {
                dispatch(updateNodeProperty({ propertyPath: ["nodeSize", "height"], value: NodeSizes.CUSTOM }));
            }
        }
    }, []);

    if (!node) return null;

    const styles = getStyle(node, path, stateMode, cvModeIndex);
    const stylePath = getStylePath(path, stateMode, cvModeIndex);

    const sizeOptions = Object.entries(NodeSizes).reduce((options, [key, value]) => {
        if (!excluded?.includes(value) && !baseLayerExclude.includes(value)) {
            options[value] = key[0] + key.slice(1, key.length).toLowerCase();
        }
        return options;
    }, {} as Record<string, string>);

    const parentDirection = useAppSelector(getParentDirection);
    const overflowX = styles.stateStyle?.overflowX || styles.default?.overflowX;
    const overflowY = styles.stateStyle?.overflowY || styles.default?.overflowY;
    const flexGrow = styles.stateStyle?.flexGrow || styles.default?.flexGrow;

    const heightState = node?.nodeSize?.height || NodeSizes.FILL;
    const heightValue = getValueUnit(styles.stateStyle?.height || styles.default?.height);
    const heightCallback = (value: string) => {
        switch (value) {
            case NodeSizes.FILL:
                if (parentDirection === "column") {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: "0" }));
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "1" }));
                } else {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: "100%" }));
                }
                if (overflowY) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowY"], value: "", remove: true }));
                }
                break;
            case NodeSizes.FIT:
                if (parentDirection === "column" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (overflowY) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowY"], value: "", remove: true }));
                }
                dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: "fit-content" }));

                break;
            case NodeSizes.SCROLL:
                if (parentDirection === "column" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (heightState === NodeSizes.FILL || heightState === NodeSizes.FIT) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: "", remove: true }));
                }
                dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowY"], value: "auto" }));
                break;
            case NodeSizes.CUSTOM:
                if (parentDirection === "column" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (overflowY) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowY"], value: "", remove: true }));
                }
                if (heightState === NodeSizes.FILL || heightState === NodeSizes.FIT) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: "", remove: true }));
                }

                break;
        }
        dispatch(updateNodeProperty({ propertyPath: ["nodeSize", "height"], value }));
    };
    const heightValueCallback = (value: string, unit: string) => {
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "height"], value: value + unit, remove: value === "" }));
    };

    const widthState = node?.nodeSize?.width || NodeSizes.FILL;
    const widthValue = getValueUnit(styles.stateStyle?.width || styles.default?.width);
    const widthCallback = (value: string) => {
        dispatch(updateNodeProperty({ propertyPath: ["nodeSize", "width"], value }));
        switch (value) {
            case NodeSizes.FILL:
                if (parentDirection === "row") {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: "0" }));
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "1" }));
                } else {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: "100%" }));
                }
                if (overflowX) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowX"], value: "", remove: true }));
                }
                break;
            case NodeSizes.FIT:
                if (parentDirection === "row" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (overflowX) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowX"], value: "", remove: true }));
                }
                dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: "fit-content" }));

                break;
            case NodeSizes.SCROLL:
                if (parentDirection === "row" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (widthState === NodeSizes.FILL || widthState === NodeSizes.FIT) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: "", remove: true }));
                }
                dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowX"], value: "auto" }));
                break;
            case NodeSizes.CUSTOM:
                if (parentDirection === "row" && flexGrow) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "flexGrow"], value: "", remove: true }));
                }
                if (overflowX) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "overflowX"], value: "", remove: true }));
                }
                if (widthState === NodeSizes.FILL || widthState === NodeSizes.FIT) {
                    dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: "", remove: true }));
                }
                break;
        }
    };
    const widthValueCallback = (value: string, unit: string) => {
        dispatch(updateNodeProperty({ propertyPath: [...stylePath, "width"], value: value + unit, remove: value === "" }));
    };

    if (noWrapper) {
        return (
            <>
                {!excluded?.includes("height") && (
                    <>
                        <SelectInput
                            title="Height"
                            value={heightState}
                            options={sizeOptions}
                            callback={heightCallback}
                            disabled={!!forceMode?.height}
                        />
                        {(heightState === NodeSizes.SCROLL || heightState === NodeSizes.CUSTOM) && (
                            <UnitInput title="Height" value={heightValue.value} unit={heightValue.unit || "px"} callback={heightValueCallback} />
                        )}
                    </>
                )}
                {!excluded?.includes("width") && (
                    <>
                        <SelectInput title="Width" value={widthState} options={sizeOptions} callback={widthCallback} disabled={!!forceMode?.width} />
                        {(widthState === NodeSizes.SCROLL || widthState === NodeSizes.CUSTOM) && (
                            <UnitInput title="Width" value={widthValue.value} unit={widthValue.unit || "px"} callback={widthValueCallback} />
                        )}
                    </>
                )}
            </>
        );
    } else {
        return (
            <SidebarCollapse parent={parent} title={title || "Size"}>
                {!excluded?.includes("height") && (
                    <>
                        <SelectInput
                            title="Height"
                            value={heightState}
                            options={sizeOptions}
                            callback={heightCallback}
                            disabled={!!forceMode?.height}
                        />
                        {(heightState === NodeSizes.SCROLL || heightState === NodeSizes.CUSTOM) && (
                            <UnitInput title="Value" value={heightValue.value} unit={heightValue.unit || "px"} callback={heightValueCallback} />
                        )}
                    </>
                )}
                {!excluded?.includes("width") && (
                    <>
                        <SelectInput title="Width" value={widthState} options={sizeOptions} callback={widthCallback} disabled={!!forceMode?.width} />
                        {(widthState === NodeSizes.SCROLL || widthState === NodeSizes.CUSTOM) && (
                            <UnitInput title="Value" value={widthValue.value} unit={widthValue.unit || "px"} callback={widthValueCallback} />
                        )}
                    </>
                )}
            </SidebarCollapse>
        );
    }
};

export default Size;
